import React, {useContext, useEffect, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import Card from "./card/Card";
import '../base/Theme.css';
import './Game.css';
import firebase from "firebase/app";
import "firebase/firestore"
import {Hands, Messages, Players, Tables} from "../../firebase/Collections";
import {mapToHand, mapToMessage, mapToPlayer, mapToSeat, mapToTable} from "../../firebase/Mappers";
import SeatView from "./seat/SeatView";
import {call, check, fold, kick, leave, raise, sendMessage, sit, sitIn, sitOut} from "../../data/Api";
import Loader from "../base/loader/Loader";
import Text from "../base/text/Text";
import {formatValue} from "../../utils/Formatter";
import Dialog from "../base/dialog/Dialog";
import {HiChevronLeft, HiChevronRight, HiVolumeUp} from "react-icons/hi";
import Chat from "./chat/Chat";
import Message from "../../models/Message";
import {playMessage} from "../../utils/Sounds";
import {useSounds} from "../../utils/Hooks";
import Toggle from "../base/toggle/Toggle";
import {Context} from "../App";
import Actions from "./actions/Actions";
import Loaders from "react-loader-spinner";
import Space from "../base/space/Space";
import Checkbox from "../base/checkbox/Checkbox";
import {createWelcomeMessage} from "../../utils/Data";
import {showSuccess} from "../base/toaster/CustomToaster";

const coinImage = require("../../assets/coins.png").default;

const Game = () => {
    const {tableId} = useParams();

    const openedDate = new Date();
    const history = useHistory();
    const soundsAllowed = useSounds();
    const {soundsEnabled, toggleSounds} = useContext(Context);
    const [player, setPlayer] = useState(null);
    const [table, setTable] = useState(null);
    const [seats, setSeats] = useState(null);
    const [playerSeat, setPlayerSeat] = useState(null);
    const [hand, setHand] = useState(null);
    const [messages, setMessages] = useState([]);
    const [chatCollapsed, setChatCollapsed] = useState(false);
    const [isSittingOut, setSittingOut] = useState(null);
    const [loading, setLoading] = useState(false);
    const [depositDialogVisible, setDepositDialogVisible] = useState(false);

    const [isAutoCheckChecked, setAutoCheckChecked] = useState(false);
    const [isAutoCallChecked, setAutoCallChecked] = useState(false);
    const [autoCallValue, setAutoCallValue] = useState(0);
    const [isAutoCallAnyChecked, setAutoCallAnyChecked] = useState(false);
    const [isAutoFoldChecked, setAutoFoldChecked] = useState(false);

    useEffect(() => {
        watchTable();
        watchMessages();
        watchPlayer();
    }, [])

    useEffect(() => {
        watchHand();
        findCurrentPlayerSeat();
    }, [player])

    useEffect(() => {
        findCurrentPlayerSeat();
        resetAutomaticMoveCheckboxes();
    }, [seats])

    useEffect(() => {
        onPlayerSeatChanged();
    }, [playerSeat])

    useEffect(() => {
        playMessageSound();
    }, [messages])

    /**
     * Listens for current player int the database.
     */
    const watchPlayer = () => {
        firebase.auth().onAuthStateChanged(function (user) {
            if (user) {
                firebase.firestore()
                    .collection(Players.NAME)
                    .doc(user.uid)
                    .onSnapshot((doc) => {
                        const player = mapToPlayer(doc);
                        setPlayer(player);
                    });
            }
        });
    }

    /**
     * Listens for table in the database.
     */
    const watchTable = () => {
        firebase.firestore()
            .collection(Tables.NAME)
            .doc(tableId)
            .onSnapshot((doc) => {
                const table = mapToTable(doc);
                setTable(table);

                const _seats = [];
                doc.data().seats.forEach((s) => {
                    const seat = mapToSeat(s);
                    _seats.push(seat);
                });
                setSeats(_seats);

                setLoading(false);
            });
    }

    /**
     * Listens for messages in the database.
     */
    const watchMessages = () => {
        firebase.firestore()
            .collection(Messages.NAME)
            .where(Messages.TABLE_ID, "==", tableId)
            .limit(30)
            .orderBy("createdAt", "desc")
            .onSnapshot((query) => {
                const _messages = [];
                query.docs.forEach((doc) => {
                    const message = mapToMessage(doc);
                    if (message.createdAt < openedDate.toISOString()) return;
                    _messages.push(message);
                })
                _messages.push(createWelcomeMessage());
                setMessages(_messages);
            });
    }


    /**
     * Listens for hand in the database.
     */
    const watchHand = () => {
        if (!player) return;

        firebase.firestore()
            .collection(Hands.NAME)
            .where(Hands.PLAYER, "==", player?.id)
            .onSnapshot((query) => {
                if (query.size > 0) {
                    const hand = mapToHand(query.docs[0]);
                    setHand(hand);
                }
            });
    }

    /**
     * Find current player seat if current user is logged in and seating.
     */
    const findCurrentPlayerSeat = () => {
        if (!player || !seats) return;

        const playerSeat = seats.find((s) => (s.playerId === player.id));
        setPlayerSeat(playerSeat);
    }

    /**
     * Checks/unchecks auto button depending on current game state.
     */
    const resetAutomaticMoveCheckboxes = () => {
        if (!player || !seats) return;

        const maxBet = Math.max(...seats.map((s) => (s.bet)));
        const amountToCall = maxBet - playerSeat?.bet;

        if (isAutoCheckChecked && playerSeat?.bet !== maxBet) {
            setAutoCheckChecked(false);
        }
        if (isAutoCallChecked && autoCallValue !== amountToCall) {
            setAutoCallChecked(false);
        }
        if (isAutoCallAnyChecked && playerSeat?.bet === maxBet) {
            setAutoCallAnyChecked(false);
        }
    }

    /**
     * Plays message sound if needed.
     */
    const playMessageSound = () => {
        if (soundsAllowed && soundsEnabled && messages?.[0]?.playerId !== player?.id) {
            playMessage();
        }
    }

    /**
     * Calculates total pot size.
     * Bets + current pot size.
     */
    const getTotalPotSize = () => {
        let totalPotSize = table?.pot;
        seats.forEach((s) => {
            totalPotSize += s.bet
        });
        return totalPotSize;
    }

    /**
     * Checks if are players are all in.
     */
    const areAllPlayersAllIn = () => {
        let allIn = true;
        seats.forEach((s) => {
            if (s.id !== playerSeat?.id && s.playerMoney > 0 && s.inGame) allIn = false;
        });
        return allIn;
    }

    /**
     * Sends check request to the server.
     * @return {Promise<void>}
     */
    const onCheckClick = async () => {
        if (!playerSeat) return;

        setLoading(true);
        try {
            setAutoCheckChecked(false);
            await check(tableId);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends fold request to the server.
     * @return {Promise<void>}
     */
    const onFoldClick = async () => {
        if (!playerSeat) return;

        setLoading(true);
        try {
            setAutoFoldChecked(false);
            await fold(tableId);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends call request to the server.
     * @return {Promise<void>}
     */
    const onCallClick = async () => {
        if (!playerSeat) return;

        setLoading(true);
        try {
            setAutoCallChecked(false);
            setAutoCallAnyChecked(false);
            await call(tableId);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends raise request to the server.
     * @return {Promise<void>}
     */
    const onRaiseClick = async (money) => {
        if (!playerSeat) return;

        setLoading(true);
        try {
            await raise(tableId, money);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends sit request to the server.
     * @return {Promise<void>}
     */
    const onSitClick = async (seat) => {
        if (!player) {
            history.push("/login");
            return;
        }
        if (player?.money < table?.bigBlind) {
            setDepositDialogVisible(true);
            return;
        }

        if (loading) return;

        setLoading(true);
        try {
            await sit(tableId, seat.id, player?.id);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends leave request to the server.
     * @return {Promise<void>}
     */
    const onLeaveClick = async (seat) => {
        if (loading) return;

        setLoading(true);
        try {
            await leave(tableId, seat.id, player?.id);
            setHand(null);
            setPlayerSeat(null);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends kick request to the server.
     * @return {Promise<void>}
     */
    const onKickClick = async (seat) => {
        if (loading) return;

        setLoading(true);
        try {
            await kick(tableId, seat.id);
        } catch (e) {
        }
        setLoading(false);
    }

    /**
     * Sends sit out request to the server.
     * @return {Promise<void>}
     */
    const onSitOutClick = async () => {
        try {
            await sitOut(tableId);
        } catch (e) {
        }
    }

    /**
     * Sends sit in request to the server.
     * @return {Promise<void>}
     */
    const onSitInClick = async () => {
        try {
            await sitIn(tableId);
        } catch (e) {
        }
    }

    /**
     * Sends message request to the server.
     * @return {Promise<void>}
     */
    const onSendMessageClick = async (message) => {
        if (!player) {
            history.push("/login");
            return;
        }

        try {
            const tempMessage = new Message("", player.id, player.name, new Date().toISOString(), message);
            setMessages([tempMessage, ...messages]);
            await sendMessage(message, tableId);
        } catch (e) {
        }
    }

    /**
     * Returns to the lobby
     */
    const onExitClick = () => {
        history.push("/lobby");
    }

    /**
     * Shows deposit screen.
     */
    const onDepositDialogDeposit = () => {
        history.push("/deposit");
    }

    /**
     * Closes deposit dialog
     */
    const onDepositDialogDismiss = () => {
        setDepositDialogVisible(false);
    }

    /**
     * Shows/hides chat sidebar.
     */
    const onToggleChat = () => {
        setChatCollapsed(!chatCollapsed)
    }

    /**
     * Sits out/in player.
     */
    const onSitOutCheckboxClick = async () => {
        const newSittingOut = !isSittingOut;
        setSittingOut(newSittingOut);

        if (!newSittingOut) {
            await onSitInClick();
        } else {
            const sitOutTimeLimit = table?.sitOutTimeLimit / 60;
            showSuccess("Sitting out from the next round. You will be automatically kicked from the table after " + sitOutTimeLimit + " minutes.")

            await onSitOutClick();
        }
    }

    /**
     * Handles auto check click.
     */
    const onAutoCheckClick = async () => {
        setAutoCheckChecked(!isAutoCheckChecked);
        setAutoCallChecked(false);
        setAutoCallAnyChecked(false);
        setAutoFoldChecked(false);
    }

    /**
     * Handles auto call click.
     */
    const onAutoCallClick = async () => {
        setAutoCheckChecked(false);
        setAutoCallChecked(!isAutoCallChecked);
        setAutoCallAnyChecked(false);
        setAutoFoldChecked(false);

        const maxBet = Math.max(...seats.map((s) => (s.bet)));
        const callValue = maxBet - playerSeat?.bet;
        setAutoCallValue(callValue);
    }

    /**
     * Handles auto call any click.
     */
    const onAutoCallAnyClick = async () => {
        setAutoCheckChecked(false);
        setAutoCallAnyChecked(!isAutoCallAnyChecked);
        setAutoCallChecked(false);
        setAutoFoldChecked(false);
    }

    /**
     * Handles auto fold click.
     */
    const onAutoFoldClick = async () => {
        setAutoCheckChecked(false);
        setAutoCallChecked(false);
        setAutoCallAnyChecked(false);
        setAutoFoldChecked(!isAutoFoldChecked);
    }

    /**
     * Triggered when player seat has changed.
     */
    const onPlayerSeatChanged = async () => {
        setSittingOut(playerSeat?.sitOutTime);
        if (playerSeat?.isPlaying) {
            handleAutomaticActions();
        }
    }

    /**
     * Triggers check/fold/call action if player set.
     */
    const handleAutomaticActions = () => {
        if (isAutoCheckChecked) {
            onCheckClick();
        }
        if (isAutoCallChecked) {
            onCallClick();
        }
        if (isAutoCallAnyChecked) {
            onCallClick();
        }
        if (isAutoFoldChecked) {
            onFoldClick();
        }
    }

    const buildCollapseChat = () => {
        return <button className="CollapseButton" onClick={onToggleChat}>
            {(() => {
                if (chatCollapsed) return <HiChevronLeft size={16} color={"#d1d4d7"}/>
                else return <HiChevronRight size={16} color={"#d1d4d7"}/>
            })()}
        </button>
    }

    const buildExit = () => {
        return <button className="GameExit" onClick={onExitClick}>
            <HiChevronLeft size={28} color={"#d1d4d7"}/>
        </button>
    }

    const buildSound = () => {
        return <Toggle onToggle={toggleSounds} enabled={soundsEnabled}
                       icon={<HiVolumeUp size={28} color={"#d1d4d7"}/>}/>
    }

    const buildSitOut = () => {
        if (!playerSeat) return null;

        return <Checkbox checked={isSittingOut} text={"Sit out"} onClick={onSitOutCheckboxClick}/>
    }

    const buildTablePot = () => {
        if (!table?.pot) return <Space space={16}/>;
        return <div className="TablePotContainer">
            <img width={16} height={16} src={coinImage} alt="coin"/>
            <Text style="TablePot" text={formatValue(table?.pot, "")}/>
        </div>
    }

    const buildLoader = () => {
        if (!loading) return null;
        return <Loaders type="TailSpin" color="#ffffff" height={20} width={20}/>
    }

    if (!seats || !table) {
        return <div className="GameRoot"><Loader/></div>;
    }

    const card1Opacity = table?.winnerCards && !table?.winnerCards?.includes(table?.card1);
    const card2Opacity = table?.winnerCards && !table?.winnerCards?.includes(table?.card2);
    const card3Opacity = table?.winnerCards && !table?.winnerCards?.includes(table?.card3);
    const card4Opacity = table?.winnerCards && !table?.winnerCards?.includes(table?.card4);
    const card5Opacity = table?.winnerCards && !table?.winnerCards?.includes(table?.card5);

    let chatVisibility = "VisibleChat";
    if (chatCollapsed) chatVisibility = "CollapsedChat";

    let actionsVisibility = "CollapsedActions";
    if (!loading && playerSeat?.inGame && playerSeat?.status !== "fold") actionsVisibility = "VisibleActions";

    const maxBet = Math.max(...seats.map((s) => (s.bet)));

    const callValue = Math.min(maxBet - playerSeat?.bet, player?.money);
    const showCall = playerSeat?.bet !== maxBet;

    const minRaise = Math.min(maxBet + table?.bigBlind, maxBet + player?.money)
    const maxRaise = player?.money + playerSeat?.bet

    return (
        <div className="GameRoot">
            <div className="TableWrapper">
                <div className="TableToolbar">
                    <div className="TableToolbarLeft">
                        {buildExit()}
                    </div>
                    <div className="TableToolbarRight">
                        {buildLoader()}
                        <Space space={20}/>
                        {buildSitOut()}
                        <Space space={20}/>
                        {buildSound()}
                    </div>
                </div>
                <div className="TableContent">
                    <div className="Table">
                        <div className="Row SpaceBetween">
                            <SeatView
                                winnerCards={table?.winnerCards}
                                timeLimit={table?.timeLimit}
                                upper={true}
                                onTakeClick={onSitClick}
                                onLeaveClick={onLeaveClick}
                                onKickClick={onKickClick}
                                seat={seats?.[0]}
                                hand={hand}
                                currentPlayer={player}/>
                            <SeatView
                                winnerCards={table?.winnerCards}
                                timeLimit={table?.timeLimit}
                                upper={true}
                                onTakeClick={onSitClick}
                                onLeaveClick={onLeaveClick}
                                onKickClick={onKickClick}
                                seat={seats?.[1]}
                                hand={hand}
                                currentPlayer={player}/>
                            <SeatView
                                winnerCards={table?.winnerCards}
                                timeLimit={table?.timeLimit}
                                upper={true}
                                onTakeClick={onSitClick}
                                onLeaveClick={onLeaveClick}
                                onKickClick={onKickClick}
                                seat={seats?.[2]}
                                hand={hand}
                                currentPlayer={player}/>
                        </div>
                        <div className="Row Center">
                            <div className="TableContent">
                                <Text style="TableTotalPot" text={"Total " + formatValue(getTotalPotSize(), "")}/>
                                <div className="TableCards">
                                    <Card style={"TableCard1"} card={table?.card1} opacity={card1Opacity}/>
                                    <Card style={"TableCard2"} card={table?.card2} opacity={card2Opacity}/>
                                    <Card style={"TableCard3"} card={table?.card3} opacity={card3Opacity}/>
                                    <Card style={"TableCard4"} card={table?.card4} opacity={card4Opacity}/>
                                    <Card style={"TableCard5"} card={table?.card5} opacity={card5Opacity}/>
                                </div>
                                {buildTablePot()}
                            </div>
                        </div>
                        <div className="Row SpaceBetween">
                            <SeatView
                                winnerCards={table?.winnerCards}
                                timeLimit={table?.timeLimit}
                                upper={false}
                                onTakeClick={onSitClick}
                                onLeaveClick={onLeaveClick}
                                onKickClick={onKickClick}
                                seat={seats?.[5]}
                                hand={hand}
                                currentPlayer={player}/>
                            <SeatView
                                winnerCards={table?.winnerCards}
                                timeLimit={table?.timeLimit}
                                upper={false}
                                onTakeClick={onSitClick}
                                onLeaveClick={onLeaveClick}
                                onKickClick={onKickClick}
                                seat={seats?.[4]}
                                hand={hand}
                                currentPlayer={player}/>
                            <SeatView
                                winnerCards={table?.winnerCards}
                                timeLimit={table?.timeLimit}
                                upper={false}
                                onTakeClick={onSitClick}
                                onLeaveClick={onLeaveClick}
                                onKickClick={onKickClick}
                                seat={seats?.[3]}
                                hand={hand}
                                currentPlayer={player}/>
                        </div>
                    </div>
                </div>
                {
                    (() => {
                        if (player) {
                            return <div className={"ActionsContent " + actionsVisibility}>
                                <Actions
                                    isPlaying={playerSeat?.isPlaying}
                                    showAutoCheck={!showCall}
                                    showAutoCall={showCall}
                                    showAutoCallAny={true}
                                    showAutoFold={true}
                                    isAutoCheckChecked={isAutoCheckChecked}
                                    isAutoCallChecked={isAutoCallChecked}
                                    isAutoCallAnyChecked={isAutoCallAnyChecked}
                                    isAutoFoldChecked={isAutoFoldChecked}
                                    onAutoCheckClick={onAutoCheckClick}
                                    onAutoCallAnyClick={onAutoCallAnyClick}
                                    onAutoCallClick={onAutoCallClick}
                                    onAutoFoldClick={onAutoFoldClick}
                                    minRaise={minRaise}
                                    maxRaise={maxRaise}
                                    bigBlind={table?.bigBlind}
                                    showCall={showCall}
                                    callValue={callValue}
                                    allPlayersAllIn={areAllPlayersAllIn()}
                                    pot={table?.pot}
                                    isPreflop={!table?.card1}
                                    onRaiseClick={onRaiseClick}
                                    onCallClick={onCallClick}
                                    onFoldClick={onFoldClick}
                                    onCheckClick={onCheckClick}/>
                            </div>
                        }
                    })()
                }
            </div>
            {buildCollapseChat()}
            <div className={"ChatWrapper " + chatVisibility}>
                <Chat messages={messages} onSendClick={onSendMessageClick}/>
            </div>
            <Dialog
                title={"Not enough tokens :("}
                subtitle={"You don't have enough tokens to play on this table. Deposit tokens on the account address to play a game."}
                image={coinImage}
                visible={depositDialogVisible}
                onNegativeClick={onDepositDialogDismiss}
                negativeText={'Cancel'}
                onPositiveClick={onDepositDialogDeposit}
                positiveText={'Deposit'}/>
        </div>
    );
}

export default Game;

